import { computed, ComputedRef, inject, provide, Ref, ref } from "vue";

import type { Locale } from "@ctrl/controller";
import type {
  Controller,
  GraphDatabase,
  GraphDatabaseDictionary,
  GraphDatabaseDictionaryDictionary,
  GraphResult,
  ParentId,
  WorkspaceId,
} from "@ctrl/controller-rpc";

import assert from "@/assert";
import ContextMenu from "@/components/context-menu/ContextMenu.vue";
import MultipleEventsModal from "@/components/MultipleEventsModal.vue";
import RepeatModal from "@/components/RepeatModal.vue";
import SimpleModal from "@/components/SimpleModal.vue";
import { TaskParentFilter } from "@/components/task/types";
import type { CalendarsRef } from "@/composables/useCalendars";
import type {
  Direction,
  DirectionPreference,
} from "@/composables/useDirection";
import type { Menu } from "@/composables/useMenu";
import type { UseProfile } from "@/composables/useProfile";
import { ThemePreference } from "@/composables/useTheme";
import {
  logger as RendererLogger,
  type BaseLogger,
  type ModuleLogger,
} from "@/logger/renderer";
import type { Console, Router } from "@/types";
import type { Billing } from "@/utils/billing";

import type { UseAgents } from "./ai/useAgents";
import type { UseAutomationHotkeys } from "./ai/useAutomationHotkeys";
import type { UseAutomations } from "./ai/useAutomations";
import AssigneePicker from "./AssigneePicker.vue";
import type { UseCanvases } from "./canvas/useCanvases";
import DatePicker from "./DatePicker.vue";
import DestinationPicker from "./DestinationPicker.vue";
import type { GroupDisplayConfig, GroupDisplayOption } from "./graph/grouping";
import type { ActivityDisplayConfig } from "./graph/layouts/activity";
import type { CalendarDisplayConfig } from "./graph/layouts/calendar";
import type { ChartDisplayConfig } from "./graph/layouts/chart";
import type { FeedDisplayConfig } from "./graph/layouts/feed";
import type { GalleryDisplayConfig } from "./graph/layouts/gallery";
import type { NumberDisplayConfig } from "./graph/layouts/number";
import type { TimelineDisplayConfig } from "./graph/layouts/timeline";
import type { UseIntegrations } from "./integrations/useIntegrations";
import ModalConsole from "./ModalConsole.vue";
import type { Palette } from "./palette/palette";
import RecurrencePicker from "./RecurrencePicker.vue";
import StatusPicker from "./StatusPicker.vue";

/**
 * Generic function to create a provide/inject pattern with overloads.
 * Creates two overloaded functions: one for providing and one for injecting.
 * Optional options object allows customizing behavior.
 */
function createInjectProvider<T>(
  key: string,
  options: {
    toCondition?: (v: T | undefined) => unknown;
    defaultInjectValue?: T;
  } = {}
) {
  const toCondition = options.toCondition ?? ((v: T | undefined) => v);
  function injectProvider(): T;
  function injectProvider(value: T): void;
  function injectProvider(value?: T): T | void {
    if (value !== undefined) {
      provide(key, value);
    } else {
      const injected = inject<T | undefined>(key, options.defaultInjectValue);
      assert(toCondition(injected), `${key} is not injected yet`);
      return injected;
    }
  }
  return injectProvider;
}

export const clock = createInjectProvider<Ref<Date>>("clock");

/**
 * The controller's live "today" (`controller.today()`: flips at midnight),
 * loaded once by Routine.vue and shared, so consumers don't each hold their
 * own subscription.
 */
export const today = createInjectProvider<Ref<Date>>("today");

export const isContextualCaptureEnabled = createInjectProvider<Ref<boolean>>(
  "isContextualCaptureEnabled"
);

export const isMcpServerEnabled =
  createInjectProvider<Ref<boolean>>("isMcpServerEnabled");

/** Either the modal console either the dashboard console. Available everywhere. */
export const console = createInjectProvider<Ref<Console>>("console");

/** The modal console. Not available in the dashboard. */
export const consoleModal =
  createInjectProvider<Ref<InstanceType<typeof ModalConsole>>>("consoleModal");

/** The custom & default types of the user (depending on beta user) */
export const types =
  createInjectProvider<Ref<GraphDatabaseDictionary | undefined>>("types");

/**
 * The workspace scoping the current subtree. Components that know their
 * workspace provide it (object screens, database screens, views, workspace
 * settings) and must always provide a defined one; injecting falls back to
 * the personal workspace only when no ancestor scoped a workspace (e.g.
 * personal task/event/page screens).
 */
export function currentWorkspaceId(): ComputedRef<WorkspaceId>;
export function currentWorkspaceId(value: Ref<WorkspaceId>): void;
export function currentWorkspaceId(
  value?: Ref<WorkspaceId>
): ComputedRef<WorkspaceId> | void {
  if (value !== undefined) {
    provide("currentWorkspaceId", value);
  } else {
    const injected = inject<Ref<WorkspaceId> | undefined>(
      "currentWorkspaceId",
      undefined
    );
    const userProfile = profile();
    return computed(
      () => injected?.value ?? userProfile.value.user.personal.workspaceId
    );
  }
}

/** The default types of the user */
export const personalDefaultTypes = createInjectProvider<
  Ref<GraphDatabaseDictionary | undefined>
>("personalDefaultTypes");

/** The custom types of the user */
export const personalCustomTypes = createInjectProvider<
  Ref<GraphDatabaseDictionary | undefined>
>("personalCustomTypes");

export const workspaceDatabases =
  createInjectProvider<Ref<GraphDatabaseDictionaryDictionary | undefined>>(
    "workspaceDatabases"
  );

export const billing =
  createInjectProvider<Ref<Billing | undefined>>("billing");

export type FeatureState = false | "initializing" | "synchronized";

export const hasCalendar =
  createInjectProvider<Ref<FeatureState>>("hasCalendar");

export const hasContacts =
  createInjectProvider<Ref<FeatureState>>("hasContacts");

export const datePicker =
  createInjectProvider<Ref<InstanceType<typeof DatePicker>>>("datePicker");

export const recurrencePicker =
  createInjectProvider<Ref<InstanceType<typeof RecurrencePicker>>>(
    "recurrencePicker"
  );

export const destinationPicker =
  createInjectProvider<Ref<InstanceType<typeof DestinationPicker>>>(
    "destinationPicker"
  );

export const assigneePicker =
  createInjectProvider<Ref<InstanceType<typeof AssigneePicker>>>(
    "assigneePicker"
  );

/** The app-wide task status picker (long-pressed completion checkboxes);
 * `null` in windows that don't mount it (e.g. the menubar). */
export const statusPicker =
  createInjectProvider<Ref<InstanceType<typeof StatusPicker> | null>>(
    "statusPicker"
  );

export const simpleModal =
  createInjectProvider<Ref<InstanceType<typeof SimpleModal>>>("simpleModal");

export const repeatModal =
  createInjectProvider<Ref<InstanceType<typeof RepeatModal>>>("repeatModal");

export const multipleEventsModal = createInjectProvider<
  Ref<InstanceType<typeof MultipleEventsModal>>
>("multipleEventsModal");

export const pagesPaneWidth = createInjectProvider<Ref<number>>(
  "pagesPaneWidth",
  { defaultInjectValue: ref(0) }
);

export const contextMenu =
  createInjectProvider<Ref<InstanceType<typeof ContextMenu>>>("contextMenu");

export const disableObjectCreation = createInjectProvider<Ref<boolean>>(
  "disableObjectCreation"
);

/**
 * Enable open/plan actions on task blocks rendered in *read-only* editors
 * (e.g. AI conversation history). Only affects blocks backed by a real task.
 */
export const readOnlyTaskActions = createInjectProvider<Ref<boolean>>(
  "readOnlyTaskActions",
  { defaultInjectValue: ref(false) }
);

export const navigateToDefaultRoute = createInjectProvider<() => void>(
  "navigateToDefaultRoute"
);

export const controller = createInjectProvider<Controller>("controller");

export type { BaseLogger, ModuleLogger };

// Logger inject function that supports both:
// - inject.logger() - returns base logger (requires module parameter in each call)
// - inject.logger(moduleName) - returns logger with module pre-configured and a base() convenience
function loggerInject(): BaseLogger;
function loggerInject(moduleName: string): ModuleLogger;
function loggerInject(value: BaseLogger): void;
function loggerInject(
  valueOrModule?: BaseLogger | string
): BaseLogger | ModuleLogger | void {
  if (valueOrModule === undefined) {
    // inject.logger() - return base logger
    const injected = inject<BaseLogger>("logger");
    assert(injected, "logger is not injected yet");
    return injected;
  } else if (typeof valueOrModule === "string") {
    // inject.logger(moduleName) - return logger with module pre-configured
    const baseLogger = inject<BaseLogger>("logger");
    assert(baseLogger, "logger is not injected yet");
    return RendererLogger.renderer.wrapperForModule(valueOrModule);
  } else {
    // inject.logger(logger) - provide the logger
    provide("logger", valueOrModule);
  }
}

export const logger = loggerInject;

export const locale = createInjectProvider<ComputedRef<Locale>>("locale");

export const logout = createInjectProvider<() => Promise<void>>("logout");

export const isBetaTester = createInjectProvider<Ref<boolean>>("isBetaTester");

export const menu = createInjectProvider<Menu>("menu");

export const parent = createInjectProvider<Ref<ParentId | null>>("parent");

export const router = createInjectProvider<Router>("router");

export const inDashboard = createInjectProvider<boolean>("inDashboard", {
  toCondition: (v: boolean | undefined) => v !== undefined,
});

// XXX[TODO]: rework to have a more generalized: isPopupShown and isModalShown;
// take a look at useOverlayPresence.ts to refactor/recombine everything.
export const isModalShown = createInjectProvider<Ref<boolean>>("isModalShown");

export const isInputFocused =
  createInjectProvider<Ref<boolean>>("isInputFocused");

export const inboxFilter =
  createInjectProvider<Ref<TaskParentFilter>>("inboxFilter");

export const inboxBadge = createInjectProvider<Ref<boolean>>("inboxBadge");

export const themePreference =
  createInjectProvider<Ref<ThemePreference>>("themePreference");

export const theme = createInjectProvider<Ref<"light" | "dark">>("theme");

/** Layout direction preference (`ltr` / `rtl` / `system`). */
export const directionPreference = createInjectProvider<
  Ref<DirectionPreference>
>("directionPreference");

/** Resolved layout direction (`ltr` / `rtl`). Applied to `document.documentElement.dir`. */
export const direction = createInjectProvider<Ref<Direction>>("direction");

export const newVersion =
  createInjectProvider<Ref<string | undefined>>("newVersion");

export const isBlockSelected = createInjectProvider<Readonly<Ref<boolean>>>(
  "isBlockSelected",
  {
    defaultInjectValue: ref(false),
  }
);
export const loadNumber = createInjectProvider<Ref<number>>("loadNumber", {
  defaultInjectValue: ref(50),
});
export const objectId = createInjectProvider<Ref<string | undefined>>(
  "objectId",
  { defaultInjectValue: ref(undefined) }
);

export const autosave = createInjectProvider<Ref<boolean>>("autosave", {
  defaultInjectValue: ref(true),
});

export const database = createInjectProvider<Ref<GraphDatabase | undefined>>(
  "database",
  { defaultInjectValue: ref(undefined) }
);

export type CreateObjectCallback = () => Promise<string | undefined>;
export const createObjectCallback = createInjectProvider<CreateObjectCallback>(
  "createObjectCallback",
  {
    toCondition: () => true,
    defaultInjectValue: async () => undefined,
  }
);

export const blockId = createInjectProvider<string | undefined>("blockId", {
  defaultInjectValue: undefined,
  toCondition: (_v: string | undefined) => true,
});

export const columnsWidth = createInjectProvider<
  Ref<Map<string, number> | undefined>
>("columnsWidth", { defaultInjectValue: ref(undefined) });

export const blockSelector = createInjectProvider<{
  cancel: () => void;
  disable: () => void;
  enable: () => void;
}>("blockSelector", {
  toCondition: () => true,
  defaultInjectValue: {
    cancel: () => {},
    disable: () => {},
    enable: () => {},
  },
});

/**
 * Flush callbacks for pending debounced saves. Components with debounced
 * saves (like useDocumentMerge) register their flush here; Cmd+S and window
 * unload drain the set to save everything immediately (see
 * `provideFlushPendingChanges`).
 */
export type PendingChangeFlushes = Set<() => void>;
export const pendingChangeFlushes = createInjectProvider<PendingChangeFlushes>(
  "pendingChangeFlushes",
  { defaultInjectValue: new Set() }
);

export const profile = createInjectProvider<Ref<UseProfile>>("profile");

/**
 * The app-wide calendar store (see `useCalendars`): every calendar plus the
 * default one, loaded once by Routine.vue and shared, so components resolve
 * an event's calendar with a lookup instead of a `calendar.get` RPC.
 * `undefined` until the store has loaded.
 */
export const calendars = createInjectProvider<CalendarsRef>("calendars", {
  toCondition: () => true,
  defaultInjectValue: ref(undefined),
});

/**
 * The app-wide Membrane store (see `useIntegrations`): one long-lived
 * Membrane client and one cached services/connections snapshot per
 * organization, loaded once per session and refreshed only after mutations
 * in the integration settings.
 */
export const integrations =
  createInjectProvider<UseIntegrations>("integrations");

export const isWideDocument = createInjectProvider<Ref<boolean>>(
  "isWideDocument",
  { toCondition: () => true, defaultInjectValue: ref(false) }
);

export type ViewConfigPanel =
  | "group"
  | "properties"
  | "activity"
  | "feed"
  | "gallery"
  | "timeline"
  | "calendar"
  | "number"
  | "percent"
  | "chart"
  | "sort"
  | "filter";

export type ViewConfigParameters = {
  layout?: boolean;
  datasource?: boolean;
  group?: boolean;
  sort?: boolean;
  filter?: boolean;
  properties?: boolean;
  results?: boolean;
  query?: boolean;
  reset?: boolean;
  delete?: boolean;
};

export type ViewParameters = {
  header?: boolean;
  // show/hide the view config button (...)
  config?: boolean;
  // which options appear inside the config panel
  configParameters?: ViewConfigParameters;
  // show the filter shortcut button
  filter?: boolean;
  // show the sort shortcut button
  sort?: boolean;
  search?: boolean;
  new?: boolean;
  open?: boolean;
  context?: boolean;
  // show the object's type icon on each row (list/table layouts)
  icon?: boolean;
  groups?: string[];
};

/** A group a view record can be created into: the grouping column and the
 * group's raw value (e.g. `Status` / `"Todo"`, or a relation record). */
export type GroupingNewTarget = { column: string; value: GraphResult };

/**
 * View-provided creation entry point for the "+ New" buttons rendered at the
 * bottom of each group (or of the whole layout) by GraphMap. `create` opens
 * the same creation flow as the view header's New control — applying the
 * view's equality filters as prefill — additionally prefilled with the
 * given targets' values: the group a "+ New" button creates into, or
 * several columns at once (the Timeline's hover-create passes its start
 * and end dates). `enabled` mirrors the header control's visibility.
 */
export type GroupingNew = {
  enabled: Ref<boolean>;
  create: (targets?: GroupingNewTarget | GroupingNewTarget[]) => Promise<void>;
};

/** Group display state of the view's grouped layouts. */
export type ViewGroupings = {
  config: Ref<GroupDisplayConfig>;
  options: Ref<GroupDisplayOption[]>;
  /** The per-group "+ New" buttons rendered by GraphMap. */
  newButton: GroupingNew;
  /**
   * Drops the group display config persisted for the grouping on
   * `columnName`. Called by GroupConfig when the user removes a grouping,
   * so that re-grouping later starts fresh (type-editor order, then natural
   * order) instead of resurrecting a stale config.
   */
  clearStored: (columnName: string) => void;
};

/**
 * Single-record selection within a view (owned by View.vue): the selected
 * record's object id, and the entry points GraphRecord / GraphTitle call
 * on single click (`select`) or Escape / click-outside (`deselect`).
 */
export type ViewSelection = {
  selectedId: Readonly<Ref<string | undefined>>;
  select: (objectId: string) => void;
  deselect: () => void;
};

/**
 * Layout-specific display prefs currently shown by the view. Activity and
 * Feed carry extra fields; list, table, and board have none yet. Always
 * set to the active layout (unset only for non-view states like object
 * pages).
 */
export type ViewLayoutConfig =
  | { layout: "inline" }
  | { layout: "table" }
  | { layout: "board" }
  | ({ layout: "activity" } & ActivityDisplayConfig)
  | ({ layout: "feed" } & FeedDisplayConfig)
  | ({ layout: "gallery" } & GalleryDisplayConfig)
  | ({ layout: "timeline" } & TimelineDisplayConfig)
  | ({ layout: "calendar" } & CalendarDisplayConfig)
  | ({ layout: "number" } & NumberDisplayConfig)
  // Gauge and Progress reuse the Number config shape, restricted to the
  // percent functions (see `percentConfigForLayout`).
  | ({ layout: "gauge" } & NumberDisplayConfig)
  | ({ layout: "progress" } & NumberDisplayConfig)
  // The chart layouts share one config (see `chartConfigForLayout`).
  | ({ layout: "bar" } & ChartDisplayConfig)
  | ({ layout: "line" } & ChartDisplayConfig)
  | ({ layout: "donut" } & ChartDisplayConfig);

export const asActivityLayoutConfig = (
  config: ViewLayoutConfig | undefined
): ActivityDisplayConfig | undefined =>
  config?.layout === "activity" ? config : undefined;

export const asFeedLayoutConfig = (
  config: ViewLayoutConfig | undefined
): FeedDisplayConfig | undefined =>
  config?.layout === "feed" ? config : undefined;

export const asGalleryLayoutConfig = (
  config: ViewLayoutConfig | undefined
): GalleryDisplayConfig | undefined =>
  config?.layout === "gallery" ? config : undefined;

export const asTimelineLayoutConfig = (
  config: ViewLayoutConfig | undefined
): TimelineDisplayConfig | undefined =>
  config?.layout === "timeline" ? config : undefined;

export const asCalendarLayoutConfig = (
  config: ViewLayoutConfig | undefined
): CalendarDisplayConfig | undefined =>
  config?.layout === "calendar" ? config : undefined;

/**
 * Config of the aggregate-rendering layouts (Number, Gauge, Progress),
 * with the layout kept so ViewNumber can pick how to draw the value.
 */
export const asAggregateLayoutConfig = (
  config: ViewLayoutConfig | undefined
):
  | (NumberDisplayConfig & { layout: "number" | "gauge" | "progress" })
  | undefined =>
  config?.layout === "number" ||
  config?.layout === "gauge" ||
  config?.layout === "progress"
    ? config
    : undefined;

/**
 * Config of the chart layouts (Bar, Line, Donut), with the layout kept so
 * ViewChart can pick how to draw the buckets.
 */
export const asChartLayoutConfig = (
  config: ViewLayoutConfig | undefined
): (ChartDisplayConfig & { layout: "bar" | "line" | "donut" }) | undefined =>
  config?.layout === "bar" ||
  config?.layout === "line" ||
  config?.layout === "donut"
    ? config
    : undefined;

/**
 * Everything a view exposes to the layouts it renders: the resolved
 * display parameters, the grouping display state, and the single-record
 * selection. Provided only by View.vue; injecting `undefined` means "not
 * inside a view", so consumers must degrade gracefully (hide the view-only
 * affordances, treat records as unselectable).
 */
export type ViewContext = {
  parameters: Ref<ViewParameters>;
  groupings: ViewGroupings;
  selection: ViewSelection;
  /**
   * Whether the view's rows are tasks — the built-in Task type, or a custom
   * type with a task binding (see `isTaskShape` in `bindings.ts`).
   * GraphRecord surfaces a completion checkbox before the title of such
   * rows (like TaskInline).
   */
  isTaskShape: Readonly<Ref<boolean>>;
  /**
   * Opens the view config popup on a given panel. Used by the per-group
   * "…" menu (Group), the table header "…" (Properties), and the column
   * header right-click menu (Edit / Sort / Filter / Group by).
   */
  openConfig: (panel: ViewConfigPanel, column?: string) => void;
  /**
   * Opens the column-header context menu at the pointer. Board and Table
   * headers call this on right-click, passing the column so Sort / Filter
   * / Group can pre-fill it.
   */
  openColumnHeaderMenu?: (event: MouseEvent, column?: string) => void;
  /**
   * Opens the data source's type schema editor (TypeEdition). Used by the
   * table header "+" next to the properties control.
   */
  openSchema: () => void;
  /**
   * Prefix for per-view browser storage (column widths, group display, …).
   * Set by screens and query blocks that mount `View` with
   * `storage-key-prefix`.
   */
  storageKeyPrefix: Readonly<Ref<string | undefined>>;
  /**
   * The data source this view is showing, once resolved. Nested Query
   * blocks compare against this so they do not remount the same database
   * inside a row's notes.
   */
  databaseId: Readonly<Ref<string | undefined>>;
  /** Current layout extras; see {@link ViewLayoutConfig}. */
  layoutConfig: Readonly<Ref<ViewLayoutConfig | undefined>>;
  /**
   * The view's Properties whitelist, in display order. Unset means every
   * column. Layouts follow this order, overriding the type editor.
   */
  shownProperties?: Readonly<Ref<readonly string[] | undefined>>;
  /**
   * Raise the running query's `limit` and re-fetch. When Results is a
   * number, GraphMap shows every returned row and Load more uses this
   * instead of slicing the fetched set. `limit` is undefined when Results
   * is All (GraphMap then pages client-side).
   */
  pagination?: {
    limit: Readonly<Ref<number | undefined>>;
    pageSize: Readonly<Ref<number | undefined>>;
    loadMore: () => void;
  };
};

export const viewContext = createInjectProvider<ViewContext | undefined>(
  "viewContext",
  { toCondition: () => true }
);

export const palette = createInjectProvider<Palette>("palette", {
  toCondition: () => true,
  defaultInjectValue: {
    context: ref(null),
    automations: ref(undefined),
    open: () => {},
    triggerAutomation: async () => {},
    release: () => {},
  },
});

export const automations = createInjectProvider<
  Ref<UseAutomations | undefined>
>("automations", {
  defaultInjectValue: ref(undefined),
  toCondition: () => true,
});

/**
 * Per-account, device-local hotkeys bound to manual automations; the
 * app-wide listener lives in Routine.vue (see useAutomationHotkeys.ts).
 */
export const automationHotkeys = createInjectProvider<
  Ref<UseAutomationHotkeys | undefined>
>("automationHotkeys", {
  defaultInjectValue: ref(undefined),
  toCondition: () => true,
});

export const agents = createInjectProvider<Ref<UseAgents | undefined>>(
  "agents",
  {
    defaultInjectValue: ref(undefined),
    toCondition: () => true,
  }
);

export const canvases = createInjectProvider<Ref<UseCanvases | undefined>>(
  "canvases",
  {
    defaultInjectValue: ref(undefined),
    toCondition: () => true,
  }
);
